import type { AssetSourceRow } from './asset-reconciliation-worktable';
import { Alert, Stack } from '@mui/material';
import { useReconciliationSyncControls } from './reconciliation-sync-controls';

const quantity = (row: AssetSourceRow) => row.kind === 'MissingInSalesforce' ? 0 : row.salesforceActiveQuantity;
const mode = (row: AssetSourceRow) => row.syncMode ?? (quantity(row) === 0 ? 'Stop' : 'Update');

export function assetSyncBlockReason(row: AssetSourceRow): string | null {
  if (row.syncBlockReason !== null) return row.syncBlockReason ?? 'IncompleteSource';
  if ((row.pendingOrders?.length ?? 0) > 0) return 'PendingOrder';
  if ((row.duplicateWebkuaLicenseNumbers?.length ?? 0) > 0) return 'MultipleTargets';
  if (!row.companyNumber?.trim() || !row.salesforceContractNumber?.trim() || !row.itemId?.trim()
    || row.webkuaContractIds?.length !== 1 || !row.webkuaContractIds[0]?.trim()
    || !row.webkuaContractNumber?.trim()) return 'AmbiguousLink';
  if (!['QuantityMismatch', 'ActivationMismatch', 'MissingInSalesforce', 'MissingInWebkua'].includes(row.kind))
    return 'UnsupportedDifference';
  const targetQuantity = quantity(row);
  if (targetQuantity == null || !Number.isInteger(targetQuantity)
    || targetQuantity < 0 || targetQuantity > 2147483647) return 'InvalidQuantity';
  if (['CreateLicense', 'CreateItem'].includes(mode(row))) {
    if (targetQuantity <= 0) return 'InvalidQuantity';
    if ((row.webkuaActiveQuantity ?? 0) !== 0) return 'MultipleTargets';
    if (mode(row) === 'CreateItem' && !row.syncLicenseId?.trim()) return 'AmbiguousLink';
    if (row.syncItemLineId || (mode(row) === 'CreateLicense' && row.syncLicenseId)) return 'AmbiguousLink';
    return null;
  }
  if (row.kind === 'MissingInWebkua') return 'IncompleteSource';
  const items = row.webkuaItems?.filter((item) => item.isActive && item.activeQuantity > 0) ?? [];
  if (items.length !== 1) return 'MultipleTargets';
  if (!items[0].licenseId?.trim() || !items[0].itemLineId?.trim()) return 'AmbiguousLink';
  if (row.syncMode && (row.syncLicenseId !== items[0].licenseId || row.syncItemLineId !== items[0].itemLineId))
    return 'AmbiguousLink';
  if (items[0].activeQuantity !== row.webkuaActiveQuantity || items[0].itemId !== row.itemId)
    return 'MultipleTargets';
  return null;
}

const target = (row: AssetSourceRow) => {
  const item = row.webkuaItems?.find((value) => value.isActive && value.activeQuantity > 0);
  return {
    companyNumber: row.companyNumber, salesforceContractNumber: row.salesforceContractNumber, itemId: row.itemId,
    webkuaContractId: row.webkuaContractIds?.[0], webkuaContractNumber: row.webkuaContractNumber,
    licenseId: row.syncMode ? row.syncLicenseId ?? '' : item?.licenseId ?? '',
    itemLineId: row.syncMode ? row.syncItemLineId ?? '' : item?.itemLineId ?? '',
  };
};
const differences = (row: AssetSourceRow) => [{
  field: 'quantity', salesforceValue: quantity(row) == null ? null : String(quantity(row)),
  webkuaValue: String(row.webkuaActiveQuantity ?? 0),
}];
const reductions = (row: AssetSourceRow) => quantity(row) != null && row.webkuaActiveQuantity != null
  && Number(quantity(row)) < row.webkuaActiveQuantity ? ['quantity'] : [];

export function useAssetSyncControls(onRefresh?: () => void) {
  return useReconciliationSyncControls<AssetSourceRow>({
    entity: 'assets', namespace: 'salesForceAssetReconciliationSync',
    rowKey: (row) => Object.values(target(row)).join('|'),
    identityField: 'targetKey', identity: (row) => Object.values(target(row)).join('|'),
    label: (row) => row.itemName || row.itemId,
    target, visible: () => true, blockReason: assetSyncBlockReason,
    blockedTooltip: (row, text) => text(`sync.errors.${assetSyncBlockReason(row) ?? 'NoDifferences'}`),
    differences, usesClears: true, clearedFields: reductions,
    warningText: ([quantity], text) => text(quantity.salesforceValue === '0' ? 'sync.stopWarning' : 'sync.reduceWarning',
      { from: quantity.webkuaValue, to: quantity.salesforceValue }),
    validatePreview: (preview) => {
      const difference = preview.differences[0];
      if (!preview.assetSync || preview.differences.length !== 1 || difference.field !== 'quantity' ||
        difference.salesforceValue == null || difference.webkuaValue == null ||
        !/^\d+$/.test(difference.salesforceValue) || !/^\d+$/.test(difference.webkuaValue)) return false;
      const target = Number(difference.salesforceValue);
      const existing = Number(difference.webkuaValue);
      if (!Number.isInteger(target) || target < 0 || target > 2147483647 ||
        !Number.isInteger(existing) || existing < 0) return false;
      return preview.assetSync.mode === 'Stop' ? target === 0 && existing > 0 &&
        preview.clearedFields.includes('quantity') :
        preview.assetSync.mode === 'Update' ? target > 0 && existing > 0 :
          target > 0 && existing === 0 && preview.clearedFields.length === 0;
    },
    previewContent: (preview, text) => <Stack spacing={1}>
      {preview.assetSync && <Alert severity="info">{text(`sync.actions.${preview.assetSync.mode}`)}</Alert>}
      {preview.assetSync?.mode === 'Stop' && preview.assetSync.lastUsedDate2026 && <Alert severity="warning">
        {text('sync.usedWarning', { date: preview.assetSync.lastUsedDate2026.slice(0, 10) })}
      </Alert>}
    </Stack>,
    tooltip: (row, text) => {
      if (mode(row) === 'CreateLicense' || mode(row) === 'CreateItem')
        return text(`sync.actions.${mode(row)}`, { quantity: quantity(row) });
      if (mode(row) !== 'Stop') return null;
      return [text('sync.stopWarning', { from: row.webkuaActiveQuantity, to: 0 }),
        ...(row.lastUsedDate2026 ? [text('sync.usedWarning', { date: row.lastUsedDate2026.slice(0, 10) })] : [])].join(' ');
    },
    fieldName: (field, text) => text(`fields.${field}`), value: (_, value) => value ?? '-',
    valueColumns: ['salesforceValue', 'webkuaValue'],
  }, onRefresh);
}
